<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="computedTitle"
    dialog-width="50%"
    max-height="80vh"
    body-height="70vh"
    @agree="agreeAction"
    :loading="loading"
    :is-valid="isValid"
    :agree-text="computedAgree"
  >
    <template #body>
      <template v-if="isFetched">
        <v-col cols="12">
          <v-form ref="form" v-model="isValid">
            <expression-builder
              ref="expressionBuilder"
              v-model="payload.filter_expression"
              :mainDataSource.sync="payload.data_source"
              :type="scope"
              :is-data-source-changable="isDataSourceChangable"
              admin
            >
              <template #inputs>
                <v-row no-gutters>
                  <v-col cols="9">
                    <main-input-dialog-row
                      v-for="input in inputs"
                      v-bind="input"
                      :key="input.name"
                      v-model="payload[input.name]"
                    />
                  </v-col>
                </v-row>
              </template>
            </expression-builder>
            <layer-adding-permissions
              class="mt-5"
              :value="payload.permissions"
              @input="onInput"
              @mainInput="onMainInput"
              @attachmentsInput="onAttachmentsInput"
              type="features_layer"
            />
          </v-form>
        </v-col>
      </template>
      <main-loader v-else></main-loader>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import ExpressionBuilder from '@/components/ExpressionBuilder';
import MainLoader from '@/components/MainLoader';
import LayerAddingPermissions from '@/components/LayerAddingPermissions';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'LayerAddingDialog',
  components: {
    MainLargeDialog,
    MainInputDialogRow,
    ExpressionBuilder,
    MainLoader,
    LayerAddingPermissions,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    scope: {
      type: String,
      default: '',
    },
    layer: {
      type: Object,
      default: undefined,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isCopying: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFetched: false,
    loading: false,
    isFiltersVisible: false,
    isValid: false,
    currentLayerObject: undefined,
    filter: undefined,
    payload: {
      name: '',
      image_attribute: null,
      filter_expression: null,
      style_web: {},
      data_source: undefined,
      permissions: [],
    },
    isDataSourceChangable: false,
  }),
  computed: {
    featureLayerNameMaxLength: get('admin/settingsValues@feature_layer_name_max_length'),
    computedTitle() {
      return this.isEditing
        ? this.$i18n.t('dialog.title.manageLayer')
        : this.isCopying
        ? this.$i18n.t('dialog.title.copyLayer', { layer_name: this.layer?.name })
        : this.$i18n.t('dialog.title.addLayer');
    },
    computedAgree() {
      return this.isEditing ? 'dialog.agreeSave' : this.isCopying ? 'dialog.agreeCreate' : 'dialog.agreeAdd';
    },
    agreeAction() {
      return this.isEditing ? this.editLayer : this.isCopying ? this.copyLayer : this.addLayer;
    },
    groups: get('users/groups'),
    inputs() {
      return [
        {
          name: 'name',
          cols: '12',
          clearable: true,
          align: 'baseline',
          translationPath: 'dialog.body.layerName',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.featureLayerNameMaxLength,
          },
        },
        {
          name: 'image_attribute',
          cols: '12',
          clearable: true,
          align: 'start',
          translationPath: 'dialog.body.profilePicture',
          subtitle: 'dialog.body.profilePictureSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.selectedDataSourceHyperlinkAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.payload.data_source,
        },
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dataSources: get('layers/metadata'),
    selectedDataSource() {
      return this.dataSources[this.payload.data_source] || {};
    },
    selectedDataSourceHyperlinkAttributes() {
      return (
        this.selectedDataSource?.attributes_schema?.attributes?.filter(attr => attr.data_type.name === 'hyperlink') ||
        []
      );
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (!nV) {
          this.$refs.form?.reset();
          this.$refs.form?.resetValidation();
          this.$refs.expressionBuilder?.reset();
          this.isFetched = false;
          this.isValid = false;
          this.$emit('update:layer', undefined);
        } else {
          this.init();
        }
      },
    },
  },
  methods: {
    getLayerPermissions: call('layers/getLayerPermissions'),
    addLayerAction: call('layers/addLayer'),
    editLayerAction: call('layers/editLayer'),
    getGroups: call('users/getGroups'),
    onInput(e) {
      const index = this.payload.permissions.map(permission => permission.group_id).indexOf(e.group_id);
      this.payload.permissions.splice(index, 1, { ...e });
    },
    onAttachmentsInput(v) {
      this.payload.permissions = this.payload.permissions.map(permission => {
        return { ...permission, attachments_and_comments_value: v };
      });
    },
    onMainInput(v) {
      this.payload.permissions = this.payload.permissions.map(permission => {
        return { ...permission, main_value: v };
      });
    },
    getPermissions() {
      return this.$_objectToArray(this.groups)
        .filter(group => !group.is_admin_group)
        .map(group => {
          return { group_id: group.id, main_value: 0, attachments_and_comments_value: 0 };
        });
    },
    async addEditHandler(f, payload) {
      try {
        this.loading = true;
        await f(payload);
        this.loading = false;
        this.computedIsVisible = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async editLayer() {
      const payloadCopy = { ...this.payload };
      delete payloadCopy.data_source;
      await this.addEditHandler(this.editLayerAction, {
        layer_id: this.layer.id,
        scope: this.scope,
        layer_type: 'features_layers',
        body: {
          ...payloadCopy,
        },
      });
    },
    async addLayer() {
      await this.addEditHandler(this.addLayerAction, {
        layer_type: 'features_layers',
        body: {
          ...this.payload,
          layer_scope: this.scope,
        },
      });
    },
    async copyLayer() {
      await this.addEditHandler(this.copyLayerAction, {
        addPayload: {
          layer_type: 'features_layers',
          body: {
            ...this.payload,
            layer_scope: this.scope,
          },
        },
        editPayload: {
          scope: this.scope,
          layer_type: 'features_layers',
          body: {
            form_schema: this.layer.form_schema,
          },
        },
      });
    },
    async copyLayerAction(data) {
      const newLayerId = await this.addLayerAction(data.addPayload);
      await this.editLayerAction({
        ...data.editPayload,
        layer_id: newLayerId,
      });
    },
    async init() {
      try {
        await this.getGroups();
        if (this.isEditing || this.isCopying) {
          const { name, filter_expression, data_source_name, id, style_web, image_attribute } = this.layer;
          this.payload = {
            ...this.payload,
            name: this.isCopying ? null : name,
            filter_expression,
            data_source: data_source_name,
            permissions: [],
            image_attribute,
            ...(this.isCopying && { style_web }),
          };
          this.isDataSourceChangable = false;
          this.payload.permissions = JSON.parse(JSON.stringify((await this.getLayerPermissions(id)).data.data));
        } else {
          this.payload.permissions = JSON.parse(JSON.stringify(this.getPermissions()));
          this.isDataSourceChangable = true;
        }
        this.isFetched = true;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-list-item__title {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
}
</style>
